import {useMemo} from "react";

import {useGetArticlesByCategoryIdsQuery, useGetHomepageFeaturedEntriesQuery} from "../../newshub/api/get_entries";
import {IEntry} from "../../newshub/types/Entry";
import {IHomepageFeaturedEntryItem} from "../../newshub/types/HomepageFeaturedEntry";

export const LANDING_MARKET_NEWS_ARTICLES_COUNT = 3;
export const LANDING_MARKET_NEWS_CATEGORY_SLUG = "raporty-i-analizy";

export const LANDING_MARKET_NEWS_SUBCATEGORY_IDS = {
    RAPORTY: 27,
    NEWSLETTERS: 37,
    WEB: 38
} as const;

export const LANDING_MARKET_NEWS_SUBCATEGORY_ID_LIST = Object.values(LANDING_MARKET_NEWS_SUBCATEGORY_IDS);

const LANDING_MARKET_NEWS_SUBCATEGORY_ID_SET = new Set<number>(LANDING_MARKET_NEWS_SUBCATEGORY_ID_LIST);

const RECENT_ARTICLES_FETCH_PAGE_SIZE = 10;

const belongsToLandingMarketNewsSubcategory = (entry: IEntry): boolean =>
    entry.category?.some(({id}) => LANDING_MARKET_NEWS_SUBCATEGORY_ID_SET.has(id)) ?? false;

const mapHomepageFeaturedEntries = (results: IHomepageFeaturedEntryItem[] | undefined): IEntry[] => {
    if (!results?.length) {
        return [];
    }

    return [...results].sort((a, b) => a.position - b.position).map(({entry}) => entry);
};

export const useLandingMarketNewsArticles = () => {
    const {
        data: featuredData,
        isLoading: isFeaturedLoading,
        isError: isFeaturedError
    } = useGetHomepageFeaturedEntriesQuery();

    const featuredEntryIds = useMemo(() => {
        if (!featuredData?.results?.length) {
            return new Set<number>();
        }

        return new Set(featuredData.results.map(({entry}) => entry.id));
    }, [featuredData?.results]);

    const featuredArticles = useMemo(
        () => mapHomepageFeaturedEntries(featuredData?.results).slice(0, LANDING_MARKET_NEWS_ARTICLES_COUNT),
        [featuredData?.results]
    );

    const {
        data: recentArticlesData,
        isLoading: isRecentArticlesLoading,
        isError: isRecentArticlesError
    } = useGetArticlesByCategoryIdsQuery(
        {
            categories: LANDING_MARKET_NEWS_SUBCATEGORY_ID_LIST,
            page: 1,
            pageSize: RECENT_ARTICLES_FETCH_PAGE_SIZE
        },
        {skip: isFeaturedLoading}
    );

    const recentArticles = useMemo(() => {
        if (!recentArticlesData?.results?.length) {
            return [];
        }

        return recentArticlesData.results
            .filter((entry) => belongsToLandingMarketNewsSubcategory(entry) && !featuredEntryIds.has(entry.id))
            .slice(0, LANDING_MARKET_NEWS_ARTICLES_COUNT);
    }, [featuredEntryIds, recentArticlesData?.results]);

    const isRecentLoading = isFeaturedLoading || isRecentArticlesLoading;

    const featuredEmpty = !isFeaturedLoading && (isFeaturedError || featuredArticles.length === 0);
    const recentEmpty = !isRecentLoading && (isRecentArticlesError || recentArticles.length === 0);

    return {
        featuredArticles,
        featuredLoading: isFeaturedLoading,
        featuredEmpty,
        recentArticles,
        recentLoading: isRecentLoading,
        recentEmpty,
        articles: featuredArticles,
        isLoading: isFeaturedLoading,
        isEmpty: featuredEmpty
    };
};
